'use client';

import { clsx } from 'clsx';
import { useRouter } from 'next/navigation';
import { ChangeEvent, FC, InputHTMLAttributes } from 'react';

import { getInputAria } from 'src/general/components/forms/Input/Input.helpers';
import { InputBase } from 'src/general/components/forms/Input/Input.type';
import { FieldWrapper } from 'src/general/components/forms/Input/atoms/FieldWrapper';
import { FilterValues } from 'src/types/CataloguePage.types';

import { FilterOption, PlpFiltersConditionalProps } from '../../PlpFilters.type';

type FilterSelectOption<InputValue> = InputBase<InputValue> & {
  options: FilterOption[];
};

type Props = Prettify<
  FilterSelectOption<string | number> &
    Omit<InputHTMLAttributes<HTMLSelectElement>, 'size'> &
    PlpFiltersConditionalProps & {
      onSelectFilter?: (filter: FilterOption) => void;
    }
>;

export const FilterSelectItem: FC<Props> = ({
  className,
  placeholder,
  labelOutside,
  subLabel,
  options,
  size,
  value,
  onSelectFilter,
  'data-testid': testIdProp,
  ...props
}) => {
  const router = useRouter();
  const testId = testIdProp || 'filter-select';

  const onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    if (props.type === 'sidebar') {
      const href = e.target.options[e.target.selectedIndex].dataset.href;
      if (href) {
        router.push(href);
      }
    } else if (onSelectFilter) {
      onSelectFilter({
        slug: e.target.options[e.target.selectedIndex].dataset.slug ?? '',
        value: e.target.value,
        filter: e.target.options[e.target.selectedIndex].dataset.filter as FilterValues,
      });
    }
  };

  return (
    <FieldWrapper
      {...props}
      size={size}
      value={value}
      className={clsx(className, 'selectWrapper')}
      labelOutside={labelOutside}
      subLabel={subLabel}
    >
      <select {...getInputAria(props)} {...props} onChange={onSelect} data-testid={testId}>
        {options.map((option) => (
          <option
            data-href={option.href}
            data-slug={option.slug}
            data-filter={option.filter}
            value={option.value}
            key={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
};
